import jsonwebtoken from 'jsonwebtoken';

import { MenuGroup, MenuItem } from '@/components/utils/types';
import { monthsOfTheYear } from '@/data/constants';
import {
  adminMenuItems,
  commonMenuItemsGroup,
  menteeMenuItems,
  mentorMenuItems,
} from '@/data/mainNavigationItems';
import { MonthYearOptionShape } from '@/data/types';

/**
 * Checks if the current screen size crosses (is below) a specific Tailwind breakpoint.
 * @param breakpoint - Tailwind breakpoint prefix: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
 * @returns true if screen width is below the breakpoint, false otherwise
 */
export const isBelowBreakpoint = (
  breakpoint: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
): boolean => {
  if (typeof window === 'undefined') return false;

  const breakpoints = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536,
  };

  return window.innerWidth < breakpoints[breakpoint];
};

export const isActiveRoute = (item: MenuItem, pathname: string): boolean => {
  if (item.route === pathname) return true;
  if (item.children) {
    return item.children.some((child: MenuItem) =>
      isActiveRoute(child, pathname)
    );
  }
  return false;
};

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An error occurred';
}

export function stringifyObjectToBase64(object: unknown) {
  return btoa(JSON.stringify(object));
}

export function decodeObjectFromBase64(string: string) {
  if (string === '') {
    return null;
  }
  return JSON.parse(atob(string));
}

function getNavigationItemsByRole(userRole: string) {
  switch (userRole) {
    case 'mentor':
      return mentorMenuItems;
    case 'mentee':
      return menteeMenuItems;
    default:
      return [];
  }
}

export function mountNavigationByRole(userRole: string): MenuGroup[] {
  const roleMenu = getNavigationItemsByRole(userRole.toLowerCase());

  return [
    {
      name: '',
      menuItems: [...roleMenu, ...commonMenuItemsGroup],
    },
  ];
}

export function mountAdminNavigation(): MenuGroup[] {
  return [
    {
      name: 'Admin',
      menuItems: [...adminMenuItems],
    },
  ];
}

export type TimeSlotOption = {
  label: string; // human-readable
  value: string; // database-compatible HH:mm:ss
};

export const generateTimeSlotOptions = (
  interval: number = 15
): TimeSlotOption[] => {
  const options: TimeSlotOption[] = [];

  const pad = (num: number) => num.toString().padStart(2, '0');

  for (let minutes = 0; minutes < 24 * 60; minutes += interval) {
    const hour24 = Math.floor(minutes / 60);
    const minute = minutes % 60;

    const hour12 = hour24 % 12 || 12;
    const period = hour24 < 12 ? 'am' : 'pm';

    const label = `${hour12}:${pad(minute)} ${period}`;
    const value = `${pad(hour24)}:${pad(minute)}:00`;

    options.push({ label, value });
  }

  return options;
};

/**
 * Converts time from 24-hour format to 12-hour format with AM/PM
 * @param time - Time in 24-hour format (e.g., "22:30:00" or "09:15:00")
 *
 * @returns Formatted time in 12-hour format with AM/PM (e.g., "10:30 PM" or "9:15 AM")
 */
export const convertTimeToAmPm = (time: string): string => {
  if (!time) return '';

  const [hoursStr, minutesStr] = time.split(':');

  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  const period = hours >= 12 ? 'pm' : 'am';

  // Convert hours to 12-hour format
  const formattedHours = hours % 12 || 12; // If hours is 0, display as 12

  // Format the time (ensuring minutes has leading zero if needed)
  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const hasSearchFilters = <T>(filters: T) => {
  return (
    filters &&
    Object.keys(filters).some((key) => {
      const value = filters[key as keyof typeof filters];

      return Array.isArray(value) && value.length > 0;
    })
  );
};

export function hasNullOrEmpty(
  obj: Record<string, unknown> | null | undefined,
  exclude: string[] = []
): boolean {
  if (!obj) {
    return true;
  }

  const filteredObj = Object.fromEntries(
    Object.entries(obj).filter(([key]) => !exclude.includes(key))
  );

  return Object.values(filteredObj).some((value) => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  });
}

export function getTaskStatus(
  obj: Record<string, unknown> | null | undefined,
  exclude: string[] = []
): 'completed' | 'in-progress' {
  const incomplete = hasNullOrEmpty(obj, exclude);

  if (incomplete) {
    return 'in-progress';
  }

  return 'completed';
}

export function isMentor(userRole: string): boolean {
  return userRole.toLowerCase() === 'mentor';
}

/**
 * Converts cents to dollars and formats as currency
 * @param cents - The amount in cents (e.g., 10000, 10020)
 * @returns Formatted dollar string (e.g., "$100.00", "$100.20")
 */
export function centsToDollars(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Gets the current month and the next 3 months with their labels and years
 * Handles year transitions (e.g., December 2025 -> January 2026)
 */
export function getCurrentAndNext3Months(): MonthYearOptionShape[] {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-12
  const currentYear = now.getFullYear();

  const result: MonthYearOptionShape[] = [];

  for (let i = 0; i < 4; i++) {
    const monthIndex = (currentMonth - 1 + i) % 12;
    const monthValue = monthIndex + 1;
    const yearOffset = Math.floor((currentMonth - 1 + i) / 12);
    const year = currentYear + yearOffset;
    const monthLabel = monthsOfTheYear[monthIndex].label;

    result.push({
      label: `${monthLabel} ${year}`,
      month: monthValue,
      year,
    });
  }

  return result;
}

export function isAdminToken(idToken?: string): boolean {
  if (!idToken) {
    return false;
  }

  const decodedToken = jsonwebtoken.decode(idToken);

  if (
    decodedToken &&
    typeof decodedToken === 'object' &&
    'cognito:groups' in decodedToken
  ) {
    const cognitoGroups = (decodedToken as Record<string, Array<string>>)[
      'cognito:groups'
    ];

    return cognitoGroups.some((group) => group === 'Admin');
  }

  return false;
}

export function getRoleFromToken(idToken?: string): string | null {
  if (!idToken) {
    return null;
  }

  const decodedToken = jsonwebtoken.decode(idToken);

  if (
    decodedToken &&
    typeof decodedToken === 'object' &&
    'custom:group' in decodedToken
  ) {
    return (decodedToken as Record<string, string>)['custom:group'];
  }

  return null;
}

export function getCognitoIdFromToken(idToken?: string): string | null {
  if (!idToken) {
    return null;
  }

  const decodedToken = jsonwebtoken.decode(idToken);

  if (decodedToken && typeof decodedToken === 'object' && decodedToken.sub) {
    return decodedToken.sub;
  }

  return null;
}

export function getEmailFromToken(idToken?: string): string | null {
  if (!idToken) {
    return null;
  }

  const decodedToken = jsonwebtoken.decode(idToken);

  if (decodedToken && typeof decodedToken === 'object' && decodedToken.email) {
    return decodedToken.email;
  }

  return null;
}
